import { useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CListGroup,
  CListGroupItem,
  CRow,
  CSpinner,
  CStepper,
} from "@coreui/react-pro";
import { useSetTradingViewAccount, useTradingViewAccount } from "../../api/hooks/useTradingViewAccount";
import { useToast } from "../../context/useToast";
import { routePaths } from "../../routes/route-paths";

// CStepper's exposed imperative API.
interface CStepperRef {
  next: () => void;
  prev: () => void;
  finish: () => void;
  reset: () => void;
}

/** The rule the API and the back office hold a username to, tested on the trimmed value; mirrored so a request is never refused for it. */
const TRADINGVIEW_USERNAME_PATTERN = /^[A-Za-z0-9_]{4,25}$/;

/** Reached from Profile and from an add-on's page, so it returns to whichever sent the customer here. */
export function TradingViewUsernameWizard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showWarning } = useToast();
  const account = useTradingViewAccount();
  const setAccount = useSetTradingViewAccount();

  const stepperRef = useRef<CStepperRef>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [validated, setValidated] = useState(false);
  const [newUsername, setNewUsername] = useState("");

  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo ?? routePaths.profile;
  const currentUsername = account.data?.username || null;
  const trimmed = newUsername.trim();
  const isSameAsCurrent = currentUsername !== null && trimmed === currentUsername;
  const usernameValid = TRADINGVIEW_USERNAME_PATTERN.test(trimmed) && !isSameAsCurrent;

  const handleBack = () => {
    if (currentStep === 1) {
      navigate(returnTo);
    } else {
      stepperRef.current?.prev();
    }
  };

  const handleUsernameNext = () => {
    if (!usernameValid) {
      setValidated(true);
      return;
    }
    setValidated(false);
    stepperRef.current?.next();
  };

  const handleSubmit = async () => {
    try {
      await setAccount.mutateAsync(trimmed);
      showSuccess("Your TradingView username is saved.");
      navigate(returnTo);
    } catch (err) {
      showWarning(err instanceof Error ? err.message : "We couldn't save your TradingView username.");
    }
  };

  const header = (
    <>
      <h2>TradingView username</h2>
      <p className="text-body-secondary">
        The TradingView account we invite to your strategies' scripts when you have TradingView Scripts Club.
      </p>
      <hr />
    </>
  );

  if (account.isLoading) {
    return (
      <>
        {header}
        <div className="d-flex justify-content-center py-5">
          <CSpinner color="primary" />
        </div>
      </>
    );
  }

  if (!account.data) {
    return (
      <>
        {header}
        <CAlert color="danger">
          We couldn't load your TradingView username right now. Please try again in a moment.
        </CAlert>
      </>
    );
  }

  if (account.data.locked) {
    return (
      <>
        {header}
        <CAlert color="warning">
          Your TradingView username is locked. <Link to={routePaths.contact}>Contact us</Link> if it changes.
        </CAlert>
        <Link to={returnTo} className="btn btn-outline-secondary">
          Back
        </Link>
      </>
    );
  }

  const steps = [
    {
      label: "Username",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Username</h4>
          </CCardHeader>
          <CCardBody>
            <CAlert color="info">
              Once you have TradingView Scripts Club, your username locks. After that, contact us to change it.
            </CAlert>
            <CForm noValidate>
              <CRow className="mb-4 gy-3">
                <CCol md={6}>
                  <CFormLabel htmlFor="current-username">Current username</CFormLabel>
                  <CFormInput id="current-username" value={currentUsername ?? ""} placeholder="Not added" disabled />
                </CCol>
                <CCol md={6}>
                  <CFormLabel htmlFor="new-username">New username</CFormLabel>
                  <CFormInput
                    id="new-username"
                    value={newUsername}
                    autoComplete="off"
                    aria-describedby="new-username-rule"
                    onChange={(e) => setNewUsername(e.target.value)}
                    invalid={validated && !usernameValid}
                  />
                  <CFormFeedback invalid>
                    {isSameAsCurrent
                      ? "That's already your TradingView username."
                      : "Use letters, numbers and underscores, 4 to 25 characters."}
                  </CFormFeedback>
                  <div id="new-username-rule" className="form-text">
                    Letters, numbers and underscores, 4 to 25 characters.
                  </div>
                </CCol>
              </CRow>
            </CForm>
            <div className="d-flex justify-content-between">
              <CButton color="secondary" variant="outline" onClick={handleBack}>
                Cancel
              </CButton>
              <CButton color="primary" onClick={handleUsernameNext}>
                Next: Review
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: "Review",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Review</h4>
          </CCardHeader>
          <CCardBody>
            <p className="text-body-secondary">Check the username below, then save it.</p>
            <CListGroup className="mb-4">
              <CListGroupItem className="d-flex justify-content-between">
                <strong>Current username</strong>
                <span>{currentUsername ?? "Not added"}</span>
              </CListGroupItem>
              <CListGroupItem className="d-flex justify-content-between">
                <strong>New username</strong>
                <span>{trimmed}</span>
              </CListGroupItem>
            </CListGroup>
            <div className="d-flex justify-content-between">
              <CButton color="secondary" variant="outline" onClick={handleBack} disabled={setAccount.isPending}>
                Back
              </CButton>
              <CButton color="primary" onClick={handleSubmit} disabled={setAccount.isPending}>
                {setAccount.isPending && <CSpinner size="sm" className="me-2" />}
                Save username
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
  ];

  return (
    <>
      {header}
      <CStepper
        ref={stepperRef}
        steps={steps}
        onStepChange={setCurrentStep}
        onReset={() => setCurrentStep(1)}
      />
    </>
  );
}
